import { useEffect, useMemo, useState } from "react";
import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
} from "@/components/ui/table";

import { AdminPayment, PaymentStatus } from "@/types";

const SORTABLE_FIELDS = [
  "company",
  "user",
  "jobOpening",
  "status",
  "paidAt",
  "createdAt",
] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const STATUS_TEXT_MAP: Record<PaymentStatus, string> = {
  paid: "Paid",
  needs_review: "Needs Review",
  refunded: "Refunded",
  uncollectible: "Uncollectible",
  unknown: "Unknown",
  void: "Void",
};

const ENTRIES_PER_PAGE = 10;

const PaymentTable = ({ payments }: { payments: AdminPayment[] }) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortableField>("createdAt");
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const sortedEntries: AdminPayment[] | undefined = useMemo(() => {
    switch (sortField) {
      case "company":
      case "user":
        return [...payments].sort(
          (a, b) =>
            (a[sortField].name ?? "").localeCompare(b[sortField].name ?? "") *
            sortDirection,
        );
      case "jobOpening":
        return [...payments].sort(
          (a, b) =>
            (a[sortField].role ?? "").localeCompare(b[sortField].role ?? "") *
            sortDirection,
        );
      case "status":
      case "createdAt":
        return [...payments].sort(
          (a, b) => a[sortField].localeCompare(b[sortField]) * sortDirection,
        );
      case "paidAt":
        return [...payments].sort(
          (a, b) =>
            (a[sortField] ?? "").localeCompare(b[sortField] ?? "") *
            sortDirection,
        );
    }
  }, [payments, sortField, sortDirection]);

  const numberOfPages = useMemo(() => {
    if (!sortedEntries) {
      return 1;
    }

    return Math.ceil(sortedEntries.length / ENTRIES_PER_PAGE);
  }, [sortedEntries]);

  const currentPageEntries = useMemo(
    () =>
      sortedEntries?.slice(
        (page - 1) * ENTRIES_PER_PAGE,
        page * ENTRIES_PER_PAGE,
      ),
    [sortedEntries, page],
  );

  const onHeadingClick = (field: SortableField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection(1);
    } else {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    }
  };

  useEffect(() => {
    if (page > numberOfPages) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          <TableBody>
            <thead>
              <TableRow>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("company")}
                  sortDirection={
                    sortField === "company" ? sortDirection : undefined
                  }
                >
                  Company
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("jobOpening")}
                  sortDirection={
                    sortField === "jobOpening" ? sortDirection : undefined
                  }
                >
                  Job Opening
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("user")}
                  sortDirection={
                    sortField === "user" ? sortDirection : undefined
                  }
                >
                  Candidate
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("status")}
                  sortDirection={
                    sortField === "status" ? sortDirection : undefined
                  }
                >
                  Status
                </TableRowItem>
                <TableRowItem heading>Total Amount</TableRowItem>
                <TableRowItem heading>Candidate Fee</TableRowItem>
                <TableRowItem heading>Venturous Fee</TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("paidAt")}
                  sortDirection={
                    sortField === "paidAt" ? sortDirection : undefined
                  }
                >
                  Date Paid
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("createdAt")}
                  sortDirection={
                    sortField === "createdAt" ? sortDirection : undefined
                  }
                >
                  Processed At
                </TableRowItem>
              </TableRow>
            </thead>
            <tbody>
              {currentPageEntries?.map((payment) => (
                <AdminRow key={`payment-${payment.id}`} payment={payment} />
              ))}
            </tbody>
          </TableBody>
          {numberOfPages > 1 ? (
            <TableFooter>
              <TablePagination
                currentPage={page}
                totalPages={numberOfPages}
                navigateFn={setPage}
              />
            </TableFooter>
          ) : null}
        </TableWrapper>
      </div>
    </div>
  );
};

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const AdminRow = ({ payment }: { payment: AdminPayment }) => {
  return (
    <TableRow>
      <TableRowItem>
        <a
          href={`/new-admin/company/${payment.company.id}`}
          className="text-xs text-[#646cff] font-medium hover:underline"
        >
          {payment.company?.name ?? "Unnamed"}
        </a>
      </TableRowItem>
      <TableRowItem>
        <a
          href={`/new-admin/job/${payment.jobOpening.id}`}
          className="text-xs text-[#646cff] font-medium hover:underline"
        >
          {payment.jobOpening?.role ?? "Unnamed"}
        </a>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {payment.user?.name ?? "Unnamed"}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {STATUS_TEXT_MAP[payment.status]}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {formatter.format(payment.totalAmountCents / 100)}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {formatter.format(payment.candidateAmountCents / 100)}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {formatter.format(payment.venturousAmountCents / 100)}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {payment.paidAt
            ? new Date(payment.paidAt).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "Unknown"}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {new Date(payment.createdAt).toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </span>
      </TableRowItem>
    </TableRow>
  );
};

export default PaymentTable;
